// src/features/tasks/hooks/useTaskTemplates.js
import { useState, useCallback, useMemo } from 'react';
import { taskTemplatesAPI } from '../services/taskTemplates';
import { useApi } from '../../../hooks/api/useApi';

export function useTaskTemplates() {
  const [templates, setTemplates] = useState([]);
  const { loading, error, execute, clearError } = useApi();

  const fetchAllTemplates = useCallback(async () => {
    const result = await execute(() => taskTemplatesAPI.getAllTemplates());
    if (result.success) {
      setTemplates(result.data.data?.templates || []);
    }
    return result;
  }, [execute]);

  const searchTemplates = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return await fetchAllTemplates();
    }
    const result = await execute(() => taskTemplatesAPI.searchTemplates(searchTerm));
    if (result.success) {
      setTemplates(result.data.data?.templates || []);
    }
    return result;
  }, [execute, fetchAllTemplates]);

  const fetchById = useCallback(async (templateId) => {
    return await execute(() => taskTemplatesAPI.getById(templateId));
  }, [execute]);

  const createTemplate = useCallback(async (data) => {
    const result = await execute(() => taskTemplatesAPI.create(data));
    if (result.success) {
      await fetchAllTemplates();
    }
    return result;
  }, [execute, fetchAllTemplates]);

  const updateTemplate = useCallback(async (templateId, data) => {
    const result = await execute(() => taskTemplatesAPI.update(templateId, data));
    if (result.success) {
      setTemplates(prev =>
        prev.map(t => t.template_id === templateId ? { ...t, ...result.data.data } : t)
      );
    }
    return result;
  }, [execute]);

  const deleteTemplate = useCallback(async (templateId) => {
    const result = await execute(() => taskTemplatesAPI.delete(templateId));
    if (result.success) {
      setTemplates(prev => prev.filter(t => t.template_id !== templateId));
    }
    return result;
  }, [execute]);

  const templatesByCategory = useMemo(() => {
    const grouped = {};
    templates.forEach(template => {
      if (!grouped[template.category]) grouped[template.category] = [];
      grouped[template.category].push(template);
    });
    return grouped;
  }, [templates]);

  const categories = useMemo(() => Object.keys(templatesByCategory).sort(), [templatesByCategory]);

  const getTemplatesByCategory = useCallback((category) => {
    return templatesByCategory[category] || [];
  }, [templatesByCategory]);

  const getTemplateById = useCallback((templateId) => {
    return templates.find(t => t.template_id === templateId);
  }, [templates]);

  return {
    templates,
    templatesByCategory,
    categories,
    loading,
    error,
    fetchAllTemplates,
    searchTemplates,
    fetchById,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplatesByCategory,
    getTemplateById,
    clearError,
  };
}
